import { Car, Compass, Fish, Map, Mountain, UtensilsCrossed, Waves, type LucideIcon } from "lucide-react";

/**
 * A look for each travel-guide category: a cover photo from our own imagery,
 * an icon and an accent colour. The posts have no photos of their own, so
 * the category carries the visual weight on the index and category pages.
 */
export interface CategoryVisual {
  image: string;
  alt: string;
  icon: LucideIcon;
  /** Hex colour for rules, icons and chips. */
  accent: string;
  /** Short line under the category name on the index. */
  blurb: string;
}

const VISUALS: Record<string, CategoryVisual> = {
  "beaches-coastal-guides": {
    image: "/images/oahu/kaaawa-shoreline.jpg",
    alt: "The Ka'a'awa shoreline on Oahu's windward coast",
    icon: Waves,
    accent: "#286975",
    blurb: "Where to swim, walk and watch the sun come up along the windward coast.",
  },
  "snorkeling-water-activities": {
    image: "/homes/caldwell-house/26-hammock-over-the-water.jpg",
    alt: "Turquoise water at the point in Ka'a'awa",
    icon: Fish,
    accent: "#1B5F78",
    blurb: "Reefs, paddling and sandbars, with the gear waiting at the house.",
  },
  "hiking-outdoor-adventure": {
    image: "/homes/caldwell-house/29-mountains-behind-the-house.jpg",
    alt: "The Ko'olau range rising behind the coast",
    icon: Mountain,
    accent: "#3F6B3E",
    blurb: "Ridges, waterfalls and valleys within an hour of the door.",
  },
  "food-local-culture": {
    image: "/homes/caldwell-house/11-dining-kitchen-ocean.jpg",
    alt: "A dining table set by the water",
    icon: UtensilsCrossed,
    accent: "#8A5720",
    blurb: "Shrimp trucks, fish markets and the traditions behind them.",
  },
  "trip-planning-itineraries": {
    image: "/homes/caldwell-house/02-aerial-house-on-the-point.jpg",
    alt: "A beachfront home at the tip of a point, from the air",
    icon: Map,
    accent: "#765B32",
    blurb: "Budgets, day-by-day plans and how to make the most of a windward stay.",
  },
  "transportation-practical-guides": {
    image: "/homes/caldwell-house/31-shoreline-toward-kualoa.jpg",
    alt: "The coast road toward Kualoa",
    icon: Car,
    accent: "#5F5747",
    blurb: "Getting here, getting around and the details that make a trip easy.",
  },
};

const FALLBACK: CategoryVisual = {
  image: "/images/oahu/mokolii-aerial.jpg",
  alt: "Mokoli'i and the Ko'olau range from the air",
  icon: Compass,
  accent: "#5F5747",
  blurb: "Guides written from our homes on the windward coast.",
};

export function categoryVisual(slug: string | null | undefined): CategoryVisual {
  return (slug && VISUALS[slug]) || FALLBACK;
}

/** The image optimizer serves any stored photo resized and as WebP. */
export const blogImg = (src: string, w: number) => `/img?src=${encodeURIComponent(src)}&w=${w}&q=90`;
